import classNames from 'classnames';
import React from 'react';

import CalendarIcon from '@mui/icons-material/Event';
import HandshakeIcon from '@mui/icons-material/HandshakeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import PharmacyIcon from '@mui/icons-material/MedicationOutlined';
import ReportIcon from '@mui/icons-material/Report';
import { ClassNameMap, makeStyles } from '@mui/styles';

import {
  AppointmentStatus,
  AppointmentType,
  EncounterDirection,
  EncounterEscalatedReason,
  EncounterMethodOutcome,
  EncounterType,
} from '@vestahealthcare/common/enums';
import { translate } from '@vestahealthcare/common/i18n';
import { MemberDashboardEncounter } from '@vestahealthcare/common/models';
import {
  DATE_FORMAT_SHORT,
  DATE_FORMAT_SHORT_WITH_TIME,
  EMPTY,
} from '@vestahealthcare/common/utils/constants';

import { StatusIndicator, Table, Tooltip, UNASSIGNED } from 'styleguide-v2';

import { MentalHealthIcon } from 'dash/src/components/Icons/MentalHealthIcon';

type Props = {
  className?: string;
  data?: MemberDashboardEncounter[];
};

const getEncounterIcon = (type: string, className: string) => {
  if (AppointmentType.isCalendarType(type))
    return <CalendarIcon className={className} />;
  if (AppointmentType.isOnboardType(type))
    return <HandshakeIcon className={className} />;
  if (AppointmentType.isPharmType(type))
    return <PharmacyIcon className={className} />;
  if (AppointmentType.isReportType(type))
    return <ReportIcon className={className} />;
  if (type === EncounterType.BHI.toString())
    return <MentalHealthIcon className={className} size="small" />;
  return <LocalPhoneIcon className={className} />;
};

const getStatusDetail = (details: string) => {
  if (details?.includes(`${EncounterMethodOutcome.ESCALATED.value} - `)) {
    const [, reason, other] = details.split(' - ');
    const reasonString =
      EncounterEscalatedReason.byKey[reason]?.toString() || reason;

    const resultReason = other ? `${reasonString} (${other})` : reasonString;

    return (
      <span className="flex min-gap">
        {EncounterMethodOutcome.ESCALATED.toString()}
        <Tooltip text={resultReason}>
          <InfoOutlinedIcon color="info" />
        </Tooltip>
      </span>
    );
  }

  return details?.toString();
};

const RECENT_ENCONUNTERS_COLUMNS = (
  styles: ClassNameMap<'firstParragraph' | 'status' | 'icon'>,
) => [
  {
    headerName: 'Type',
    component: ({
      type,
      typeSecondary,
      date,
      dateTime,
    }: MemberDashboardEncounter) => (
      <>
        <p
          className={classNames(
            styles.firstParragraph,
            'bold no-margin flex middle gap',
          )}
        >
          {getEncounterIcon(type, styles.icon)}
          {type.startsWith(AppointmentType.PHARM_CON.value)
            ? typeSecondary || type?.toString()
            : type?.toString()}
        </p>
        <p className="gray no-margin semi-bold size-xs">
          {dateTime
            ? dateTime.format(DATE_FORMAT_SHORT_WITH_TIME)
            : date.format(DATE_FORMAT_SHORT)}
        </p>
      </>
    ),
    width: 225,
  },
  {
    headerName: 'Details',
    component: ({ details, employeeName }: MemberDashboardEncounter) => (
      <StatusIndicator
        className={styles.status}
        color={details ? AppointmentStatus.getColor(details) : 'success'}
        status={
          getStatusDetail(details) || EncounterDirection.INBOUND.toString()
        }
        subtitle={employeeName || UNASSIGNED.fullName}
      />
    ),
    width: 260,
  },
  {
    headerName: 'Notes',
    component: ({ notes }: MemberDashboardEncounter) =>
      notes ? (
        <Tooltip text={notes}>
          <div
            className="ellipsis-2-lines"
            style={{ maxWidth: 'calc(50vw - 48.5rem)', width: 'fit-content' }}
          >
            {notes}
          </div>
        </Tooltip>
      ) : (
        EMPTY
      ),
  },
];

const useStyles = makeStyles({
  table: {
    marginTop: 1,
  },
  icon: {
    marginTop: '-3px',
  },
  firstParragraph: {
    '&&': {
      margin: '4px 0 -2px',
    },
  },
  status: {
    '& > :first-child': {
      margin: '4px 0 0',
    },
    '& > :last-child': {
      marginTop: '-3px',
    },
  },
});

export const RecentEncountersTable = (props: Props) => {
  const styles = useStyles();

  return (
    <Table
      className={classNames(props.className, styles.table)}
      config={{
        columns: RECENT_ENCONUNTERS_COLUMNS(styles),
        compact: true,
        data: props.data || [],
        paginationOptions: [-1],
      }}
      fontSize="small"
      empty={translate('memberProfile.dashboard.noRecentEncounters')}
      whiteBackground
    />
  );
};
